import { useState, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CategorySelect } from '@/components/category/category-select'
import { toast } from 'sonner'
import { sanitizeAmountStr, sanitizeAmountNum } from '@/lib/utils'
import { validateAndToast, createRecurringSchema } from '@/lib/schemas'
import { useProfiles, useCategories, useCreateRecurring, useUpdateRecurring, usePaymentMethods } from '@/lib/hooks'
import type { RecurringTransaction } from '@/lib/hooks'
import { useAuth } from '@/lib/auth-context'
import type { Scope, Frequency, Currency } from '@/lib/db-types'

const PAYMENT_METHOD_NONE = '__none__'

type FormData = {
  name: string
  amount: string
  currency: Currency
  category_id: string
  day_of_month: string
  paid_by: string
  scope: Scope
  frequency: Frequency
  frequency_months: number[]
  payment_method_id: string
}

const emptyForm: FormData = {
  name: '',
  amount: '',
  currency: 'JPY',
  category_id: '',
  day_of_month: '1',
  paid_by: '',
  scope: 'household',
  frequency: 'monthly',
  frequency_months: [],
  payment_method_id: '',
}

const MONTH_LABELS = ['1月','2月','3月','4月','5月','6月','7月','8月','9月','10月','11月','12月'] as const

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  editingItem: RecurringTransaction | null
  onDelete: (item: RecurringTransaction) => void
  onToggleActive: (item: RecurringTransaction) => void
}

export function RecurringEditSheet({
  open,
  onOpenChange,
  editingItem,
  onDelete,
  onToggleActive,
}: Props) {
  const { data: profiles = [] } = useProfiles()
  const { data: categories = [] } = useCategories()
  const { data: paymentMethods = [] } = usePaymentMethods()
  const { user: currentUser } = useAuth()
  const createRec = useCreateRecurring()
  const updateRec = useUpdateRecurring()

  const [form, setForm] = useState<FormData>(emptyForm)

  useEffect(() => {
    if (!open) return
    if (editingItem) {
      setForm({
        name: editingItem.name,
        amount: String(editingItem.amount),
        currency: editingItem.currency,
        category_id: editingItem.category_id || '',
        day_of_month: String(editingItem.day_of_month),
        paid_by: editingItem.paid_by,
        scope: editingItem.scope,
        frequency: editingItem.frequency,
        frequency_months: editingItem.frequency_months ?? [],
        payment_method_id: editingItem.payment_method_id ?? '',
      })
    } else {
      setForm({
        ...emptyForm,
        paid_by: currentUser?.id || '',
        scope: 'household',
      })
    }
  }, [open, editingItem, currentUser?.id])

  // Filter payment_methods by scope: household recurring can only reference
  // household sources; personal recurring can reference household + own personal.
  const availablePaymentMethods = useMemo(() => {
    return paymentMethods.filter((ps) => {
      if (!ps.is_active && ps.id !== form.payment_method_id) return false
      if (form.scope === 'household') return ps.scope === 'household'
      // personal recurring
      if (ps.scope === 'household') return true
      return ps.created_by === currentUser?.id
    })
  }, [paymentMethods, form.scope, form.payment_method_id, currentUser?.id])

  // If the currently selected payment source is no longer valid for the scope,
  // clear it. (e.g. user toggled scope from personal -> household after picking a personal card)
  useEffect(() => {
    if (!form.payment_method_id) return
    const stillValid = availablePaymentMethods.some((ps) => ps.id === form.payment_method_id)
    if (!stillValid) {
      setForm((p) => ({ ...p, payment_method_id: '' }))
    }
  }, [availablePaymentMethods, form.payment_method_id])

  const handleSave = () => {
    const payload = validateAndToast(createRecurringSchema, {
      name: form.name,
      amount: sanitizeAmountNum(Number(form.amount) || 0, form.currency),
      currency: form.currency,
      category_id: form.category_id || null,
      day_of_month: Number(form.day_of_month),
      paid_by: form.scope === 'personal' ? (currentUser?.id || form.paid_by) : form.paid_by,
      scope: form.scope,
      frequency: form.frequency,
      frequency_months: form.frequency === 'yearly' ? form.frequency_months : null,
      payment_method_id: form.payment_method_id || null,
    })

    if (!payload) return

    if (editingItem) {
      updateRec.mutate(
        { id: editingItem.id, data: payload },
        {
          onSuccess: () => {
            toast.success('固定支出を更新しました')
            onOpenChange(false)
          },
        },
      )
    } else {
      createRec.mutate(payload, {
        onSuccess: () => onOpenChange(false),
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
        <SheetHeader>
          <SheetTitle>
            {editingItem ? '固定支出を編集' : '固定支出を追加'}
          </SheetTitle>
          <SheetDescription>
            {editingItem
              ? '固定支出の内容を変更します。'
              : form.frequency === 'monthly'
                ? '毎月自動で記録される固定支出を登録します。'
                : '年間で指定した月に自動で記録される固定支出を登録します。'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 px-4">
          <div className="space-y-2">
            <Label htmlFor="recurring-name">名前</Label>
            <Input
              id="recurring-name"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="例: 家賃"
              autoCapitalize="off"
              enterKeyHint="next"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="recurring-amount">金額</Label>
            <div className="flex gap-2 mb-2">
              <Button
                type="button"
                variant={form.currency === 'JPY' ? 'default' : 'outline'}
                size="sm"
                className="flex-1"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    currency: 'JPY',
                    amount: sanitizeAmountStr(prev.amount, 'JPY'),
                  }))
                }
              >
                ¥ JPY
              </Button>
              <Button
                type="button"
                variant={form.currency === 'USD' ? 'default' : 'outline'}
                size="sm"
                className="flex-1"
                onClick={() =>
                  setForm((prev) => ({ ...prev, currency: 'USD' }))
                }
              >
                $ USD
              </Button>
            </div>
            <CurrencyInput
              id="recurring-amount"
              currency={form.currency}
              value={form.amount}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, amount: e.target.value }))
              }
              placeholder={form.currency === 'USD' ? '12.99' : '80000'}
            />
          </div>

          <div className="space-y-2">
            <Label>カテゴリ</Label>
            <CategorySelect
              categories={categories}
              value={form.category_id || null}
              onChange={(v) =>
                setForm((prev) => ({ ...prev, category_id: v || '' }))
              }
              showUncategorized={false}
              onPersonalCategorySelected={() =>
                setForm((prev) => prev.scope === 'personal' ? prev : { ...prev, scope: 'personal', paid_by: currentUser?.id || prev.paid_by })
              }
            />
          </div>

          <div className="space-y-2">
            <Label>頻度</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={form.frequency === 'monthly' ? 'default' : 'outline'}
                size="sm"
                className="flex-1"
                onClick={() =>
                  setForm((prev) => ({ ...prev, frequency: 'monthly', frequency_months: [] }))
                }
              >
                毎月
              </Button>
              <Button
                type="button"
                variant={form.frequency === 'yearly' ? 'default' : 'outline'}
                size="sm"
                className="flex-1"
                onClick={() =>
                  setForm((prev) => ({ ...prev, frequency: 'yearly' }))
                }
              >
                年間
              </Button>
            </div>
            {form.frequency === 'yearly' && (
              <>
                <p className="text-xs text-muted-foreground">月は複数選択できます。すべての月に適用する場合は「毎月」を選択してください。</p>
                <div className="grid grid-cols-6 gap-1.5 pt-1">
                  {MONTH_LABELS.map((label, idx) => {
                    const month = idx + 1
                    const selected = form.frequency_months.includes(month)
                    return (
                      <Button
                        key={month}
                        type="button"
                        variant={selected ? 'default' : 'outline'}
                        size="sm"
                        className="h-8 text-xs px-0"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            frequency_months: selected
                              ? prev.frequency_months.filter((m) => m !== month)
                              : [...prev.frequency_months, month].sort((a, b) => a - b),
                          }))
                        }
                      >
                        {label}
                      </Button>
                    )
                  })}
                </div>
              </>
            )}
          </div>

          <div className="space-y-2">
            <Label>引き落とし日</Label>
            <Select
              value={form.day_of_month}
              onValueChange={(value) =>
                setForm((prev) => ({ ...prev, day_of_month: value }))
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="日を選択" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <SelectItem key={day} value={String(day)}>
                    {day}日
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>区分</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={form.scope === 'household' ? 'default' : 'outline'}
                size="sm"
                className="flex-1"
                onClick={() =>
                  setForm((prev) => ({ ...prev, scope: 'household' }))
                }
              >
                家計
              </Button>
              <Button
                type="button"
                variant={form.scope === 'personal' ? 'default' : 'outline'}
                size="sm"
                className="flex-1"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    scope: 'personal',
                    paid_by: currentUser?.id || prev.paid_by,
                  }))
                }
              >
                個人
              </Button>
            </div>
          </div>

          {form.scope !== 'personal' && (
            <div className="space-y-2">
              <Label>支払者</Label>
              <div className="flex gap-2">
                {profiles.map((profile) => (
                  <Button
                    key={profile.id}
                    type="button"
                    variant={
                      form.paid_by === profile.id ? 'default' : 'outline'
                    }
                    size="sm"
                    className="flex-1"
                    onClick={() =>
                      setForm((prev) => ({ ...prev, paid_by: profile.id }))
                    }
                  >
                    {profile.display_name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>支払い方法</Label>
            <Select
              value={form.payment_method_id || PAYMENT_METHOD_NONE}
              onValueChange={(v) =>
                setForm((prev) => ({
                  ...prev,
                  payment_method_id: v === PAYMENT_METHOD_NONE ? '' : v,
                }))
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="未設定" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PAYMENT_METHOD_NONE}>未設定</SelectItem>
                {availablePaymentMethods.map((ps) => (
                  <SelectItem key={ps.id} value={ps.id}>
                    {ps.name}
                    {ps.type === 'credit_card' && ps.last4 && ` (•••• ${ps.last4})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <SheetFooter>
          <Button
            className="w-full"
            onClick={handleSave}
            disabled={createRec.isPending || updateRec.isPending || !form.name.trim() || !form.amount || !form.paid_by}
          >
            {createRec.isPending || updateRec.isPending ? '保存中...' : '保存'}
          </Button>
          {editingItem && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  onToggleActive(editingItem)
                  onOpenChange(false)
                }}
              >
                {editingItem.is_active ? '停止' : '再開'}
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => {
                  onDelete(editingItem)
                  onOpenChange(false)
                }}
              >
                削除
              </Button>
            </div>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
